/**
 * Czysta logika Bąbelków (WP12d) — bez Phaser.
 * Gwiazdki = popy; tylko rosną; brak fail-state; runda 90 s, stałe tempo spawnu.
 * Nagrody liczy wyłącznie serwer via RunClient (tu tylko stats do finishRun).
 */
import { MINIGAMES } from '@pieskowo/shared';

export const BUBBLES_GAME_ID = MINIGAMES.bubbles.id;
export const BUBBLES_DURATION_MS = 90_000;
/** Stałe tempo spawnu bąbelków (~1 / 0,9 s). */
export const BUBBLES_SPAWN_INTERVAL_MS = 900;
export const BUBBLES_MAX_STARS = MINIGAMES.bubbles.maxStars;
/** Próg „dobrej gry” = goodStarsThreshold z shared (≥12 popów). */
export const BUBBLES_GOOD_POPS = MINIGAMES.bubbles.goodStarsThreshold;
/** Promień ≥32 → średnica ≥64 (UiKit.MIN_TOUCH). */
export const BUBBLES_MIN_RADIUS = 36;
export const BUBBLES_MAX_RADIUS = 52;

/** Emotikony zabawek w bąbelkach (zero-reading). */
export const TOY_EMOJIS = ['🦴', '🎾', '🧸', '⭐', '💖', '🎈', '🍪', '🌸'] as const;

export type BubblesRunStats = {
  stars: number;
  score: number;
  durationMs: number;
  taps: number;
};

export type BubblesState = {
  startedAtMs: number;
  durationLimitMs: number;
  pops: number;
  stars: number;
  score: number;
  taps: number;
  finished: boolean;
  elapsedMs: number;
};

export function createBubblesState(
  nowMs: number,
  durationLimitMs = BUBBLES_DURATION_MS,
): BubblesState {
  return {
    startedAtMs: nowMs,
    durationLimitMs,
    pops: 0,
    stars: 0,
    score: 0,
    taps: 0,
    finished: false,
    elapsedMs: 0,
  };
}

export function tickElapsed(state: BubblesState, nowMs: number): BubblesState {
  if (state.finished) return state;
  const elapsedMs = Math.max(0, Math.floor(nowMs - state.startedAtMs));
  return { ...state, elapsedMs };
}

export function remainingMs(state: BubblesState): number {
  return Math.max(0, state.durationLimitMs - state.elapsedMs);
}

export function isTimeUp(state: BubblesState): boolean {
  return state.elapsedMs >= state.durationLimitMs;
}

export function shouldSpawn(
  state: BubblesState,
  lastSpawnMs: number,
  nowMs: number,
  intervalMs = BUBBLES_SPAWN_INTERVAL_MS,
): boolean {
  if (state.finished || isTimeUp(state)) return false;
  return nowMs - lastSpawnMs >= intervalMs;
}

/** Pop bąbelka — gwiazdki i score tylko rosną (clamp do maxStars). */
export function registerPop(state: BubblesState): BubblesState {
  if (state.finished) return state;
  const pops = state.pops + 1;
  const stars = Math.min(BUBBLES_MAX_STARS, state.stars + 1);
  const score = state.score + 1;
  const taps = state.taps + 1;
  return { ...state, pops, stars, score, taps };
}

/** Pusty tap (nie bąbelek) — licznik tapów, zero kary. */
export function registerMissTap(state: BubblesState): BubblesState {
  if (state.finished) return state;
  return { ...state, taps: state.taps + 1 };
}

export function finishRound(state: BubblesState): BubblesState {
  if (state.finished) return state;
  return { ...state, finished: true };
}

/** UI: „dobra gra” wg progu shared — NIE liczy zł (to robi serwer). */
export function isGoodGame(popsOrStars: number): boolean {
  return popsOrStars >= BUBBLES_GOOD_POPS;
}

export function buildRunStats(state: BubblesState): BubblesRunStats {
  return {
    stars: state.stars,
    score: state.score,
    durationMs: state.elapsedMs,
    taps: state.taps,
  };
}

export function formatTimerPl(ms: number): string {
  const sec = Math.ceil(ms / 1000);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function toyEmojiAt(index: number): string {
  return TOY_EMOJIS[((index % TOY_EMOJIS.length) + TOY_EMOJIS.length) % TOY_EMOJIS.length]!;
}

/** Pozycja spawnu w polu gry (margin od krawędzi HUD). */
export function spawnPosition(
  rng: () => number,
  gameW: number,
  gameH: number,
  radius: number,
): { x: number; y: number } {
  const marginX = radius + 24;
  const topHud = 88;
  const bottomHud = 96;
  const minY = topHud + radius;
  const maxY = gameH - bottomHud - radius;
  const minX = marginX;
  const maxX = gameW - marginX;
  const x = minX + rng() * Math.max(0, maxX - minX);
  const y = minY + rng() * Math.max(0, maxY - minY);
  return { x, y };
}

export function bubbleRadius(rng: () => number): number {
  return BUBBLES_MIN_RADIUS + rng() * (BUBBLES_MAX_RADIUS - BUBBLES_MIN_RADIUS);
}
